import { compact } from "lodash";
import { DateTime, IANAZone } from "luxon";
import moment from "moment-timezone";

const now = () => DateTime.now().toMillis();

export const INVALID_TIMEZONE = "Invalid Timezone";

export function getTimeZoneDisplayName(zoneName: string) {
  const zone = IANAZone.create(zoneName);

  if (!zone.isValid) {
    console.error(`"${zoneName}" is not a valid IANA time zone!`);
    return INVALID_TIMEZONE;
  }

  const offset = zone.formatOffset(now(), "short");
  const abbr = zone.offsetName(now(), { format: "short" });
  const offsetAbbr = abbr.match(/GMT/i);
  const name = zone.name.replace(/\//g, " / ").replace(/_/g, " ");
  const fullNameLabel = compact([!offsetAbbr && abbr, name]).join(" - ");

  return `(GMT${offset}) ${fullNameLabel}`;
}

export function getTimeZones() {
  const timeZones: { [s: string]: string } = {};
  const tz = moment.tz as any;

  // unpack moment tz Zone objects
  // filter out timezones that aren't in modern format
  // sort by display name
  const zones = Object.values(tz._zones)
    .map((value: string) => {
      try {
        return tz.unpack(value);
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean)
    .filter((zone: any) => zone.name.indexOf("/") >= 0)
    .sort((a, b) => {
      if (moment.tz(a.name).utcOffset() === moment.tz(b.name).utcOffset()) {
        // Ex. Compare Atlantic / Azores vs Atlantic / Cape Verde
        return getTimeZoneDisplayName(a.name).localeCompare(getTimeZoneDisplayName(b.name));
      } else {
        // Ex. Compare GMT -1:00 vs GMT -2:00 to preserve order of -12 GMT to +14 GMT
        return moment.tz(a.name).utcOffset() < moment.tz(b.name).utcOffset() ? -1 : 1;
      }
    });

  // from moment zones, create key/value of display name to value
  zones.forEach((zone: any) => {
    const zoneDisplayName = getTimeZoneDisplayName(zone.name);
    timeZones[zoneDisplayName] = zone.name;
  });

  return timeZones;
}

export const TimeZones: { [s: string]: string } = getTimeZones();
